import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { invoke } from '@tauri-apps/api/core';
import { open as openDialog } from '@tauri-apps/plugin-dialog';
import { DATASETS, openInClaude, coworkProjectExists, launchClaudeWithProject } from '../../services/openInClaudeService';
import './OpenInClaudeDialog.css';

const ROOT_STORAGE_KEY = 'atlas_project_root';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  brand: string;
  isUltimateParent: boolean;
}

export default function OpenInClaudeDialog({ isOpen, onClose, brand, isUltimateParent }: Props) {
  const [selected, setSelected] = useState<Set<string>>(new Set(DATASETS.map(d => d.key)));
  const [busy, setBusy] = useState(false);
  const [exists, setExists] = useState(false);
  const [root, setRoot] = useState<string>('');
  const [donePath, setDonePath] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSelected(new Set(DATASETS.map(d => d.key)));
      setDonePath(null);
      // Resolve root: saved choice → else default (home/Atlas) from Rust
      const saved = localStorage.getItem(ROOT_STORAGE_KEY);
      if (saved) {
        setRoot(saved);
      } else {
        invoke<string>('default_project_root').then(setRoot).catch(() => setRoot(''));
      }
      coworkProjectExists(brand, root || undefined).then(setExists).catch(() => setExists(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, brand]);

  const chooseFolder = async () => {
    const picked = await openDialog({ directory: true, multiple: false, defaultPath: root || undefined });
    if (typeof picked === 'string') {
      setRoot(picked);
      localStorage.setItem(ROOT_STORAGE_KEY, picked);
      coworkProjectExists(brand, picked).then(setExists).catch(() => setExists(false));
    }
  };

  if (!isOpen) return null;

  const toggle = (key: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleOpen = async () => {
    if (selected.size === 0) {
      toast.error('Select at least one dataset');
      return;
    }
    setBusy(true);
    const t = toast.loading('Exporting data and preparing Claude project...');
    try {
      const path = await openInClaude({
        brand,
        isUltimateParent,
        selectedKeys: Array.from(selected),
        root: root || undefined,
      });
      toast.success('Project ready', { id: t });
      setDonePath(path);
      // Deep-link Claude straight into a Cowork session with the project attached
      launchClaudeWithProject(path).catch(() => {});
    } catch (e: any) {
      console.error('Open in Claude failed:', e);
      // Surface the real error (Tauri command rejections come through as strings).
      const msg = e?.message || (typeof e === 'string' ? e : JSON.stringify(e));
      toast.error(`Open in Claude failed: ${msg || 'unknown error'}`, { id: t, duration: 12000 });
    } finally {
      setBusy(false);
    }
  };

  // Use the OS-native separator for the displayed path: backslash on Windows
  // (inferred from the root Rust hands us, or the UA), forward slash elsewhere.
  const sep = root.includes('\\') || navigator.userAgent.includes('Windows') ? '\\' : '/';
  const folderName = brand.toLowerCase().replace(/ /g, '-');
  const savePath = `${root || 'Default (home folder)'}${sep}brands${sep}${folderName}`;

  return (
    <div className="oic-overlay" onClick={onClose}>
      <div className="oic-dialog" onClick={e => e.stopPropagation()}>
        <div className="oic-header">
          <h2>{donePath ? 'Project ready ✓' : `Open ${brand} in Claude`}</h2>
          <button className="oic-close" onClick={onClose}>✕</button>
        </div>

        {donePath ? (
          <div className="oic-success">
            <p className="oic-success-path"><code>{donePath}</code></p>
            <p className="oic-success-note">
              Your data and skills are exported. Claude is opening with this
              folder attached:
            </p>
            <ol className="oic-success-steps">
              <li>In <strong>Claude</strong>, confirm folder access when prompted</li>
              <li>Ask: <em>"Make a sales proposal for {brand}"</em></li>
            </ol>
            <div className="oic-footer">
              <button className="oic-btn-secondary" onClick={() => launchClaudeWithProject(donePath)}>
                Reopen in Claude
              </button>
              <button className="oic-btn-primary" onClick={onClose}>Done</button>
            </div>
          </div>
        ) : (
        <>
        <p className="oic-subtitle">
          Choose which data to pull into the Claude project.
          {exists && <span className="oic-exists"> A project for this brand already exists — it will be updated.</span>}
        </p>

        <div className="oic-location">
          <span className="oic-location-label">Save to</span>
          <span className="oic-location-path" title={savePath}>{savePath}</span>
          <button className="oic-location-change" onClick={chooseFolder} disabled={busy}>Change…</button>
        </div>

        <div className="oic-list">
          {DATASETS.map(d => (
            <label key={d.key} className="oic-item">
              <input
                type="checkbox"
                checked={selected.has(d.key)}
                onChange={() => toggle(d.key)}
              />
              <span className="oic-item-text">
                <span className="oic-item-label">{d.label}</span>
                <span className="oic-item-desc">{d.description}</span>
              </span>
            </label>
          ))}
        </div>

        <div className="oic-footer">
          <button className="oic-btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="oic-btn-primary" onClick={handleOpen} disabled={busy}>
            {busy ? 'Preparing…' : exists ? 'Update & Open in Claude' : 'Open in Claude'}
          </button>
        </div>
        </>
        )}
      </div>
    </div>
  );
}
